import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useTenant } from "../lib/tenant";
import { TopBar } from "../components/TopBar";
import { TicketDisplay } from "../components/TicketDisplay";
import { InstallBanner } from "../components/InstallBanner";
import { useTickets, useLoyaltyConfig, usePurchaseHistory } from "../lib/tickets";
import { uploadAndCreatePurchase } from "../lib/purchases";
import { api, callableErrorMessage } from "../lib/api";
import { track } from "../lib/analytics";
import { useActiveVoucher } from "../lib/ambassador";
import { readPendingRef, clearPendingRef } from "../lib/pendingJoin";
import { compressImage } from "../lib/imageCompress";
import { CustomerQrCard } from "../components/CustomerQrCard";
import { useCashierPurchaseNotifier } from "../lib/cashierNotifier";
import { NameCaptureCard } from "../components/NameCaptureCard";

export function Home() {
  const nav = useNavigate();
  const { user, claims } = useAuth();
  const { tenant, loading: tenantLoading } = useTenant();
  const fileRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [pickedFile, setPickedFile] = useState<File | null>(null);
  const [discountCode, setDiscountCode] = useState("");
  const [uploading, setUploading] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [feedback, setFeedback] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  const { unredeemedCount, setOptimistic } = useTickets();
  const { ticketsForReward, rewardLabel } = useLoyaltyConfig();
  const { purchases } = usePurchaseHistory(5);
  const activeVoucher = useActiveVoucher();
  const isComplete = unredeemedCount >= ticketsForReward;
  const [activatingCode, setActivatingCode] = useState(false);

  const firstName = user?.displayName?.split(" ")[0] ?? user?.email?.split("@")[0] ?? "";

  const [showNameCapture, setShowNameCapture] = useState(false);
  useCashierPurchaseNotifier((p) => {
    const amount = new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      maximumFractionDigits: 0
    }).format(p.amountFinal);
    const detail = p.amountDiscount > 0 ? ` con descuento aplicado` : "";
    setFeedback({
      kind: "ok",
      text: `Compra registrada por el cajero: ${amount}${detail}. Sumaste 1 ticket.`
    });
    if (!user?.displayName) {
      setShowNameCapture(true);
    }
  });

  // Auto-activar codigo embajador si vino por URL (?ref=DEMOAMB10)
  useEffect(() => {
    if (!user || !claims.tenantId) return;
    if (activeVoucher) return;
    const pendingRef = readPendingRef();
    if (!pendingRef) return;
    setDiscountCode(pendingRef);
    (async () => {
      try {
        const res = await api.activateDiscountVoucher({ code: pendingRef });
        const expiresIn = Math.max(
          0,
          Math.round((new Date(res.data.expiresAt).getTime() - Date.now()) / 60000)
        );
        setFeedback({
          kind: "ok",
          text: `Codigo ${res.data.code} activado automaticamente. ${res.data.discountPercent}% off valido ${expiresIn} min.`
        });
        setDiscountCode("");
        clearPendingRef();
        track("voucher_autoactivated", { code: pendingRef });
      } catch {
        // Si falla la auto-activacion (codigo invalido) lo dejamos en el input
        track("voucher_autoactivate_failed", { code: pendingRef });
      }
    })();
  }, [user, claims.tenantId, activeVoucher]);

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) {
      const compressed = await compressImage(f);
      setPickedFile(compressed);
      setFilePreview(URL.createObjectURL(compressed));
      setFeedback(null);
    }
  };

  const clearPicked = () => {
    if (filePreview) URL.revokeObjectURL(filePreview);
    setPickedFile(null);
    setFilePreview(null);
    if (fileRef.current) fileRef.current.value = "";
    if (galleryRef.current) galleryRef.current.value = "";
  };

  const submitPurchase = async () => {
    if (!pickedFile || !claims.tenantId || !user) return;
    setUploading(true);
    setFeedback(null);
    track("purchase_upload_started");
    try {
      const result = await uploadAndCreatePurchase({
        tenantId: claims.tenantId,
        uid: user.uid,
        file: pickedFile,
        discountCode: discountCode.trim() || undefined
      });
      track("purchase_upload_completed", {
        status: result.status,
        amount: result.amount ?? 0,
        ticket_emitted: result.ticketEmitted
      });
      clearPicked();
      setDiscountCode("");
      if (result.ticketEmitted) {
        // Update optimista para que el card se refresque al toque (sin esperar snapshot)
        setOptimistic(result.ticketsCount);
        setFeedback({
          kind: "ok",
          text: `¡Boleta registrada! Sumaste 1 ticket (ahora tienes ${result.ticketsCount}).`
        });
      } else {
        setFeedback({
          kind: "ok",
          text:
            "Tu boleta quedó en revisión. El admin del comercio la validará en las próximas 24 h."
        });
      }
    } catch (err) {
      track("purchase_upload_failed", {
        code: (err as { code?: string })?.code ?? "unknown"
      });
      setFeedback({ kind: "err", text: callableErrorMessage(err) });
    } finally {
      setUploading(false);
    }
  };

  const onActivateCode = async () => {
    if (!discountCode.trim()) return;
    setActivatingCode(true);
    setFeedback(null);
    try {
      const res = await api.activateDiscountVoucher({ code: discountCode.trim() });
      const expiresIn = Math.max(
        0,
        Math.round((new Date(res.data.expiresAt).getTime() - Date.now()) / 60000)
      );
      setFeedback({
        kind: "ok",
        text: `Código ${res.data.code} activado. ${res.data.discountPercent}% off válido por ${expiresIn} min.`
      });
      setDiscountCode("");
      track("voucher_activated", { code: res.data.code });
    } catch (err) {
      setFeedback({ kind: "err", text: callableErrorMessage(err) });
    } finally {
      setActivatingCode(false);
    }
  };

  const onRedeem = async () => {
    setRedeeming(true);
    setFeedback(null);
    track("reward_redeem_started", { tickets: unredeemedCount });
    try {
      await api.redeemTickets();
      track("reward_redeem_completed");
      setFeedback({
        kind: "ok",
        text: `🎉 ¡Reward canjeado! Acércate a la caja para retirarlo.`
      });
    } catch (err) {
      track("reward_redeem_failed", {
        code: (err as { code?: string })?.code ?? "unknown"
      });
      setFeedback({ kind: "err", text: callableErrorMessage(err) });
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <>
      <TopBar />
      <main className="bg-cream pb-32 pt-4">
        <div className="mx-auto flex max-w-md flex-col gap-5 px-5">
          <header>
            <p className="text-xs uppercase tracking-[0.2em] text-leaf-600">Bienvenido</p>
            <h1 className="mt-1 font-display text-3xl font-semibold text-leaf-950">
              Hola{firstName ? `, ${firstName}` : ""}
            </h1>
          </header>

          {tenantLoading && (
            <section className="card text-sm text-leaf-700">Cargando tu comercio…</section>
          )}

          {tenant && (
            <section
              className="overflow-hidden rounded-3xl p-5 shadow-soft ring-1"
              style={{
                background: `linear-gradient(135deg, ${tenant.theme.primary}f0, ${tenant.theme.primary}d0)`,
                borderColor: `${tenant.theme.primary}55`,
                color: "#ffffff"
              }}
            >
              <p className="text-xs uppercase tracking-wider text-white/80">Tu loyalty card</p>
              <h2 className="mt-1 font-display text-2xl font-semibold">{tenant.name}</h2>
              <p className="mt-1 text-sm text-white/90">
                {isComplete
                  ? "¡Tu reward está listo para canjear!"
                  : `Junta ${ticketsForReward} compras y gana 1 gratis.`}
              </p>

              <div className="mt-4">
                <TicketDisplay
                  count={unredeemedCount}
                  max={ticketsForReward}
                  rewardLabel={rewardLabel}
                />
              </div>

              {isComplete && (
                <button
                  onClick={onRedeem}
                  disabled={redeeming}
                  className="btn-primary mt-4 w-full bg-white text-leaf-800 hover:opacity-95"
                >
                  {redeeming ? "Canjeando…" : "Canjear mi reward"}
                </button>
              )}
            </section>
          )}

          {user?.isAnonymous && (
            <section className="rounded-3xl border-2 border-dashed border-leaf-300 bg-leaf-50/60 p-5">
              <div className="flex items-start gap-3">
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-leaf-100">
                  <svg viewBox="0 0 24 24" className="h-5 w-5 text-leaf-700" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16l7-3 7 3z" />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-display text-lg font-semibold text-leaf-950">
                    Guarda tu progreso
                  </h3>
                  <p className="mt-1 text-sm text-leaf-800">
                    Tus tickets se guardan solo en este celular. Vincula tu cuenta con Google para
                    recuperarlos en otros dispositivos.
                  </p>
                  <button
                    onClick={() => nav("/upgrade")}
                    className="btn-primary mt-3 w-full"
                  >
                    Guardar mi cuenta
                  </button>
                  <p className="mt-2 text-center text-[11px] text-leaf-600">
                    Puedes vincular con Google o crear un email + contraseña
                  </p>
                </div>
              </div>
            </section>
          )}

          {showNameCapture && (
            <NameCaptureCard onDone={() => setShowNameCapture(false)} />
          )}

          {activeVoucher ? (
            <CustomerQrCard
              voucher={activeVoucher}
              themeColor={tenant?.theme.primary ?? "#4a6921"}
            />
          ) : (
            <section className="card flex flex-col gap-3">
              <div>
                <p className="text-xs uppercase tracking-wider text-leaf-600">Código de embajador</p>
                <h3 className="mt-1 font-display text-lg font-semibold text-leaf-950">
                  ¿Tienes un código de descuento?
                </h3>
                <p className="mt-1 text-sm text-leaf-700">
                  Actívalo antes de pagar para tener descuento.
                </p>
              </div>
              <div className="flex gap-2">
                <input
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                  placeholder={`${tenant?.tenantId.toUpperCase().slice(0, 6) ?? "CODE"}-XXXX`}
                  className="input font-mono text-sm uppercase"
                />
                <button
                  onClick={onActivateCode}
                  disabled={activatingCode || !discountCode.trim()}
                  className="btn-primary px-5 disabled:opacity-50"
                >
                  {activatingCode ? "…" : "Activar"}
                </button>
              </div>
            </section>
          )}

          <section className="card flex flex-col gap-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-leaf-600">Después de tu compra</p>
              <h3 className="mt-1 font-display text-lg font-semibold text-leaf-950">
                Sube tu boleta para sumar un punto
              </h3>
              <p className="mt-1 text-sm text-leaf-700">
                Saca foto de la boleta donde se vea el RUT del comercio.
              </p>
            </div>

            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={onPickFile}
              className="hidden"
            />
            <input
              ref={galleryRef}
              type="file"
              accept="image/*"
              onChange={onPickFile}
              className="hidden"
            />

            {filePreview ? (
              <div className="overflow-hidden rounded-2xl ring-1 ring-leaf-100">
                <img src={filePreview} alt="Boleta" className="block max-h-80 w-full object-contain bg-leaf-50" />
                <div className="flex border-t border-leaf-100">
                  <button
                    onClick={clearPicked}
                    disabled={uploading}
                    className="flex-1 py-3 text-sm text-leaf-700 hover:bg-leaf-50 disabled:opacity-50"
                  >
                    Cambiar
                  </button>
                  <button
                    onClick={submitPurchase}
                    disabled={uploading}
                    className="flex-1 border-l border-leaf-100 bg-leaf-700 py-3 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    {uploading ? "Validando…" : "✓ Registrar compra"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => fileRef.current?.click()}
                  className="flex flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-leaf-200 bg-leaf-50/50 py-6 text-leaf-700 hover:bg-leaf-50"
                >
                  <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                  <span className="text-xs font-medium">Sacar foto</span>
                </button>
                <button
                  onClick={() => galleryRef.current?.click()}
                  className="flex flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-leaf-200 bg-leaf-50/50 py-6 text-leaf-700 hover:bg-leaf-50"
                >
                  <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="9" cy="9" r="2" />
                    <path d="M21 15l-5-5L5 21" />
                  </svg>
                  <span className="text-xs font-medium">Elegir de galería</span>
                </button>
              </div>
            )}

            {feedback && (
              <div
                className={`rounded-2xl px-4 py-3 text-sm ring-1 ${
                  feedback.kind === "ok"
                    ? "bg-leaf-50 text-leaf-900 ring-leaf-200"
                    : "bg-red-50 text-red-700 ring-red-200"
                }`}
              >
                {feedback.text}
              </div>
            )}
          </section>

          {purchases.length > 0 && (
            <section className="card flex flex-col gap-2">
              <p className="text-xs uppercase tracking-wider text-leaf-600">Últimas compras</p>
              {purchases.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between border-t border-leaf-100 pt-2 first:border-0 first:pt-0"
                >
                  <div>
                    <p className="text-sm font-medium text-leaf-950">
                      {p.amount != null
                        ? new Intl.NumberFormat("es-CL", {
                            style: "currency",
                            currency: "CLP",
                            maximumFractionDigits: 0
                          }).format(p.amount)
                        : "Sin monto"}
                    </p>
                    <p className="text-[11px] text-leaf-500">
                      {p.createdAt
                        ? `${p.createdAt.toLocaleDateString("es-CL", { day: "2-digit", month: "short" })} · ${p.createdAt.toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" })}`
                        : "—"}
                    </p>
                  </div>
                  <PurchaseStatusBadge status={p.status} />
                </div>
              ))}
            </section>
          )}

          {claims.role === "customer" && (
            <section className="rounded-3xl bg-gradient-to-br from-leaf-50 to-leaf-100/60 p-5 ring-1 ring-leaf-200">
              <p className="text-xs uppercase tracking-wider text-leaf-700">Programa de embajadores</p>
              <h3 className="mt-1 font-display text-lg font-semibold text-leaf-950">
                ¿Tienes seguidores en Instagram?
              </h3>
              <p className="mt-1 text-sm text-leaf-800">
                Vuélvete embajador de {tenant?.name ?? "este comercio"} y gana comisión por cada
                compra que refieras.
              </p>
              <button
                onClick={() => nav("/postular-embajador")}
                className="btn-secondary mt-3 w-full"
              >
                Postular a embajador
              </button>
            </section>
          )}

          {claims.role === "ambassador" && (
            <section
              onClick={() => nav("/embajador")}
              className="cursor-pointer rounded-3xl bg-white p-5 ring-1 ring-leaf-100 transition hover:bg-leaf-50/50"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-leaf-600">Embajador</p>
                  <h3 className="mt-1 font-display text-lg font-semibold text-leaf-950">
                    Ir a mi panel
                  </h3>
                  <p className="mt-1 text-sm text-leaf-700">
                    Mira tus comisiones, códigos y referidos.
                  </p>
                </div>
                <svg className="h-5 w-5 text-leaf-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </div>
            </section>
          )}
        </div>
      </main>
      <InstallBanner />
    </>
  );
}

function PurchaseStatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; fg: string; label: string }> = {
    approved: { bg: "bg-leaf-100", fg: "text-leaf-800", label: "Aprobada" },
    pending_admin_review: { bg: "bg-amber-100", fg: "text-amber-800", label: "En revisión" },
    rejected: { bg: "bg-red-100", fg: "text-red-700", label: "Rechazada" }
  };
  const s = styles[status] ?? { bg: "bg-gray-100", fg: "text-gray-600", label: status };
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${s.bg} ${s.fg}`}>
      {s.label}
    </span>
  );
}
